// =============================================================================
//  COMPTEURS DE NUMÉROTATION — écran GÉNÉRIQUE du CORE. ⛔ Rien d'OMV ici.
// =============================================================================
//  🔴 CE QU'IL RÉPARE. `core/sequences` portait son moteur (incrément atomique,
//  règles de reset, alignement MAX+1, prévisualisation), ses routes et ses
//  garde-fous — et AUCUN fichier frontend ne référençait `api/sequences`
//  (mesuré le 02/09). Le compteur qui décide des numéros de facture n'était
//  visible NULLE PART. Même défaut que FactureService : un moteur complet sans
//  chemin utilisateur.
//
//  🔑 LA RÈGLE QUE CET ÉCRAN REND LISIBLE : le numéro n'est consommé QU'À
//  L'ÉMISSION EFFECTIVE. Ouvrir cet écran, lire un aperçu, changer un préfixe :
//  ⛔ AUCUN de ces gestes ne consomme un rang. L'aperçu LIT le compteur.
//
//  ⚠️ BRIQUE CORE, réutilisable telle quelle par tout tenant : aucun slug, aucun
//  libellé métier, aucun préfixe en dur. La portée tenant vient du serveur.
// =============================================================================
import { useCallback, useEffect, useState } from "react";
import { Loader2, Lock, Unlock, AlertTriangle, Eye, Save } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";
const BASE = `${API}/api/sequences`;

// Libellés d'affichage. ⚠️ Le serveur rend des types techniques ; on ne les
// affiche pas bruts, et on ⛔ n'invente pas de type absent de sa réponse.
const LIBELLES = {
  FACTURE: "Factures", AVOIR: "Avoirs", DEVIS: "Devis",
  DOSSIER: "Dossiers", CLIENT: "Clients",
};

const RESET_LIBELLES = {
  yearly: "Annuelle — le compteur repart à 1 chaque année",
  monthly: "Mensuelle — le compteur repart à 1 chaque mois",
  never: "Jamais — la série est continue",
};

const carte = {
  background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)",
  borderRadius: 12, padding: 16,
};
const etiquette = { fontSize: 12, color: "var(--oh-text-secondary)", fontWeight: 500,
                    margin: 0 };
const valeur = { margin: 0, color: "var(--oh-text)", fontVariantNumeric: "tabular-nums" };
const champ = {
  height: 36, borderRadius: 8, padding: "0 10px", fontSize: 13,
  background: "var(--oh-bg-surface)", color: "var(--oh-text)",
  border: "1px solid var(--oh-border)", width: "100%",
};

export default function CompteursNumerotation({ tenantId = null }) {
  const [lot, setLot] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [occupe, setOccupe] = useState(false);
  const [edition, setEdition] = useState({});   // type → préfixe en cours de saisie

  const charger = useCallback(async () => {
    setErreur(null);
    try {
      const r = await fetch(`${BASE}/tableau`, {
        credentials: "include",
        headers: tenantId ? { "X-Tenant-ID": tenantId } : {},
      });
      const corps = await r.json();
      if (!r.ok) throw new Error(corps.detail || `Compteurs indisponibles (${r.status})`);
      setLot(corps);
      setEdition({});
    } catch (e) { setErreur(e.message); }
  }, [tenantId]);

  useEffect(() => { charger(); }, [charger]);

  /** ⚠️ Seul le PRÉFIXE est modifiable ici, et seulement si le serveur le dit.
   *  ⛔ L'écran ne rejoue pas la règle du verrou : il lit `champs_modifiables`.
   *  Un seuil réimplémenté côté navigateur diverge, et c'est la version
   *  affichée qui gagne — jusqu'au refus incompréhensible. */
  const enregistrerPrefixe = async (ligne) => {
    const nouveau = (edition[ligne.document_type] ?? "").trim();
    if (!nouveau || nouveau === ligne.prefixe) return;
    setOccupe(true); setErreur(null);
    try {
      const r = await fetch(`${BASE}/${ligne.document_type}`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json",
                   ...(tenantId ? { "X-Tenant-ID": tenantId } : {}) },
        body: JSON.stringify({ prefix: nouveau }),
      });
      const corps = await r.json();
      if (!r.ok) { setErreur(corps.detail || `Refus (${r.status})`); return; }
      await charger();
    } catch (e) { setErreur(e.message); } finally { setOccupe(false); }
  };

  if (erreur && !lot) {
    return (
      <div style={{ ...carte, background: "var(--oh-stat-red-bg)",
                    color: "var(--oh-stat-red-text)", fontSize: 13 }}
           data-testid="compteurs-erreur">{erreur}</div>
    );
  }
  if (!lot) {
    return (
      <div style={{ display: "flex", gap: 8, alignItems: "center",
                    color: "var(--oh-text-secondary)", fontSize: 13 }}>
        <Loader2 style={{ width: 15, height: 15 }} /> Chargement des compteurs…
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: 12 }} data-testid="compteurs-numerotation">
      {/* 🔑 LA RÈGLE, DITE À L'ÉCRAN. Un utilisateur qui ne sait pas quand un
          numéro se consomme n'ose pas toucher à cet écran — ou pire, y touche
          en croyant que c'est sans effet. */}
      <div style={{ ...carte, background: "var(--oh-info-bg)",
                    color: "var(--oh-info-text)", fontSize: 13, lineHeight: 1.6 }}
           data-testid="compteurs-regle">
        <strong>Un numéro n'est consommé qu'à l'émission effective d'un document.</strong>
        {" "}Ouvrir cet écran, lire un aperçu ou changer un préfixe ne consomme
        aucun numéro. Les aperçus ci-dessous sont indicatifs : ils peuvent
        changer si quelqu'un émet un document entre-temps.
      </div>

      {erreur && (
        <div style={{ ...carte, background: "var(--oh-stat-red-bg)",
                      color: "var(--oh-stat-red-text)", fontSize: 13 }}
             data-testid="compteurs-erreur">{erreur}</div>
      )}

      <p style={{ ...etiquette }} data-testid="compteurs-portee">
        Portée : tenant <strong style={{ color: "var(--oh-text)" }}>{lot.tenant_id}</strong>
        {" "}— chaque tenant a ses propres compteurs, et chaque type de document le sien.
      </p>

      {(lot.compteurs || []).map((l) => {
        const modifiable = (l.champs_modifiables || []).includes("prefix");
        return (
          <div key={l.document_type} style={carte}
               data-testid={`compteur-${l.document_type}`}>
            <div style={{ display: "flex", alignItems: "center", gap: 10,
                          marginBottom: 12, flexWrap: "wrap" }}>
              <span style={{ fontSize: 15, fontWeight: 500, color: "var(--oh-text)" }}>
                {LIBELLES[l.document_type] || l.document_type}
              </span>
              {l.configuree ? (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5,
                               padding: "2px 8px", borderRadius: 6, fontSize: 11,
                               background: l.verrouillee ? "var(--oh-stat-amber-bg)"
                                                         : "var(--oh-stat-green-bg)",
                               color: l.verrouillee ? "var(--oh-stat-amber-text)"
                                                    : "var(--oh-stat-green-text)" }}>
                  {l.verrouillee ? <Lock style={{ width: 11, height: 11 }} />
                                 : <Unlock style={{ width: 11, height: 11 }} />}
                  {l.verrouillee ? "Série figée" : "Modifiable"}
                </span>
              ) : (
                <span style={{ padding: "2px 8px", borderRadius: 6, fontSize: 11,
                               background: "var(--oh-bg-surface)",
                               color: "var(--oh-text-secondary)" }}>
                  Pas encore configuré
                </span>
              )}
            </div>

            {!l.configuree ? (
              <p style={{ ...etiquette, lineHeight: 1.6 }}>
                {l.message}
                {l.apercu_prochain && (
                  <> Aperçu du premier document :{" "}
                    <strong style={{ color: "var(--oh-text)" }}>{l.apercu_prochain}</strong>.
                  </>
                )}
              </p>
            ) : (
              <>
                <div style={{ display: "grid", gap: 10,
                              gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))" }}>
                  <div><p style={etiquette}>Format</p>
                    <p style={{ ...valeur, fontFamily: "monospace", fontSize: 13 }}>
                      {l.format}</p></div>
                  {/* 🔑 DERNIER CONSOMMÉ et PROCHAIN DISPONIBLE sont DEUX nombres.
                      Les confondre fait lire un rang de travers — et « dernier »
                      n'existe pas tant que rien n'a été émis. */}
                  <div><p style={etiquette}>Dernier rang consommé</p>
                    <p style={valeur} data-testid={`dernier-${l.document_type}`}>
                      {l.dernier_rang_consomme ?? "— aucun document émis"}</p></div>
                  <div><p style={etiquette}>Prochain rang disponible</p>
                    <p style={{ ...valeur, fontWeight: 500 }}
                       data-testid={`prochain-${l.document_type}`}>
                      {l.prochain_rang}</p></div>
                  <div><p style={etiquette}>Remise à zéro</p>
                    <p style={{ ...valeur, fontSize: 13 }}>
                      {RESET_LIBELLES[l.regle_reset] || l.regle_reset}</p></div>
                  <div><p style={etiquette}>Documents émis</p>
                    <p style={valeur}>{l.documents_emis ?? 0}</p></div>
                  <div><p style={etiquette}>Dernier numéro généré</p>
                    <p style={{ ...valeur, fontSize: 13 }}>
                      {l.dernier_numero_genere || "—"}</p></div>
                </div>

                {/* L'APERÇU DU PROCHAIN DOCUMENT — la référence Pennylane. */}
                <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 8,
                              background: "var(--oh-accent-bg)",
                              display: "flex", alignItems: "center", gap: 8 }}
                     data-testid={`apercu-${l.document_type}`}>
                  <Eye style={{ width: 14, height: 14, color: "var(--oh-accent-text)" }} />
                  <span style={{ fontSize: 12.5, color: "var(--oh-accent-text)" }}>
                    Prochain document :{" "}
                    <strong style={{ fontFamily: "monospace" }}>{l.apercu_prochain}</strong>
                  </span>
                </div>

                {/* ⚠️ LE MOTIF DU VERROU EST DIT AVANT LE CLIC — et il dit aussi
                    ce qui RESTE possible. Un refus qui ne nomme pas l'issue
                    ressemble à une panne. */}
                {l.motif_verrou && (
                  <div style={{ marginTop: 10, padding: "10px 12px", borderRadius: 8,
                                background: "var(--oh-stat-amber-bg)",
                                color: "var(--oh-stat-amber-text)", fontSize: 12.5,
                                lineHeight: 1.6 }}
                       data-testid={`verrou-${l.document_type}`}>
                    <AlertTriangle style={{ width: 14, height: 14, verticalAlign: "-2px" }} />
                    {" "}{l.motif_verrou}
                  </div>
                )}

                <div style={{ marginTop: 12, display: "flex", gap: 8,
                              alignItems: "flex-end", flexWrap: "wrap" }}>
                  <div style={{ flex: "1 1 220px" }}>
                    <label style={etiquette} htmlFor={`prefixe-${l.document_type}`}>
                      Préfixe
                    </label>
                    <input id={`prefixe-${l.document_type}`} style={champ}
                           data-testid={`prefixe-${l.document_type}`}
                           disabled={!modifiable || occupe}
                           placeholder="Préfixe du numéro"
                           value={edition[l.document_type] ?? l.prefixe ?? ""}
                           onChange={(e) => setEdition((p) => ({
                             ...p, [l.document_type]: e.target.value }))} />
                  </div>
                  <button type="button" disabled={!modifiable || occupe}
                          data-testid={`enregistrer-${l.document_type}`}
                          onClick={() => enregistrerPrefixe(l)}
                          style={{ display: "inline-flex", alignItems: "center", gap: 6,
                                   height: 36, padding: "0 14px", borderRadius: 8,
                                   fontSize: 13, border: "none",
                                   cursor: modifiable && !occupe ? "pointer" : "not-allowed",
                                   opacity: modifiable && !occupe ? 1 : 0.5,
                                   background: "var(--oh-accent-solid)",
                                   color: "var(--oh-accent-contrast)" }}>
                    <Save style={{ width: 14, height: 14 }} /> Enregistrer
                  </button>
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
